import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { withDevtools, withDevToolsStub } from '@angular-architects/ngrx-toolkit';
import { ApiService } from '../../core/api/api.service';
import { API_ENDPOINTS } from '../../core/api/endpoints';
import { ApiError } from '../../core/api/api.models';
import { environment } from '../../../environments/environment';
import { LocationStore, reloadOnLocationChange } from '../../core/store/location.store';
import {
  APPOINTMENTS_KPI_MODULE,
  extractTodaysAppointmentCount,
  GetKpiCardsResponse,
  TODAYS_APPOINTMENTS_KPI_ID,
} from '../models/appointments-count.model';

export interface AppointmentsCountState {
  /** Today's booked appointment count for the sidebar's Appointments nav badge.
   * Stays 0 until the first load resolves, on API failure, or when there are
   * genuinely no appointments today — never a fabricated/stale number. */
  todaysCount: number;
  isLoading: boolean;
  loadError: string | null;
}

export const INITIAL_APPOINTMENTS_COUNT_STATE: AppointmentsCountState = {
  todaysCount: 0,
  isLoading: false,
  loadError: null,
};

/**
 * Owns the sidebar's "Appointments" nav badge — today's real booked-appointment count
 * from `Report/GetKpiCards` (`appts`). `AppointmentsCountApiService` is a thin delegate
 * onto this store.
 */
export const AppointmentsCountStore = signalStore(
  { providedIn: 'root' },
  environment.production ? withDevToolsStub('appointments-count') : withDevtools('appointments-count'),
  withState<AppointmentsCountState>(INITIAL_APPOINTMENTS_COUNT_STATE),
  withMethods((store) => {
    const api = inject(ApiService);
    const locationStore = inject(LocationStore);

    /** Bumped at the start of every `loadTodaysAppointmentCount()` call. Each call captures the
     * id it was given and checks it again once its response arrives — if a newer call has since
     * started (e.g. the user switched clinics again, or a mutation elsewhere triggered its own
     * refresh, before this request settled), this one is stale and must not patch state. Without
     * this, two overlapping requests can resolve out of order and let the OLDER response — for a
     * location/moment that's no longer current — silently overwrite the newer, correct one: the
     * sidebar badge then shows another location's count, or 0 from a request that raced ahead of
     * a real one. Same pattern as `latestLoadId` in appointments.store.ts. */
    let latestRequestId = 0;

    return {
      /** Restores this store to its initial state — called by StoreResetService on logout, so the
       *  sidebar badge doesn't briefly show the previous session's count before its own reload. */
      reset(): void {
        patchState(store, INITIAL_APPOINTMENTS_COUNT_STATE);
      },

      /** Load today's booked appointment count for the current clinic location. */
      loadTodaysAppointmentCount(): void {
        const requestId = ++latestRequestId;
        patchState(store, { isLoading: true, loadError: null });

        api
          .get<GetKpiCardsResponse>(API_ENDPOINTS.common.getKpiCards, {
            params: {
              ids: TODAYS_APPOINTMENTS_KPI_ID,
              module: APPOINTMENTS_KPI_MODULE,
              locationId: locationStore.locationId(),
            },
          })
          .subscribe({
            next: (response) => {
              if (requestId !== latestRequestId) return;
              patchState(store, {
                isLoading: false,
                todaysCount: extractTodaysAppointmentCount(response),
              });
            },
            error: (err: ApiError) => {
              if (requestId !== latestRequestId) return;
              patchState(store, {
                isLoading: false,
                loadError: err.message || "Failed to load today's appointment count.",
                todaysCount: 0,
              });
            },
          });
      },
    };
  }),
  // Keeps the badge correct if the user switches clinics without navigating away.
  withHooks({
    onInit(store) {
      reloadOnLocationChange(() => store.loadTodaysAppointmentCount());
    },
  }),
);
